import type { PostureStatus } from '@/components/ui/posture-chip';
import { cn } from '@/lib/utils/cn';
import { formatDayMonth } from '@/lib/utils/format';
import { ChartTooltip, useChartTooltip } from './chart-tooltip';
import { POSTURE_LABEL } from './shared';

/**
 * PostureStrip — a domain's rating at each snapshot, then today (prototype
 * `postureStrip()`): one small cell per frozen rating, oldest first, and a
 * wider ringed cell for the live rating. Sits inline in a domain tile
 * (`md`) or a rail row (`sm`), so it is an image with a label that reads
 * every rating out, not a table (a table cannot sit inside a button).
 */
export interface PostureStripCell {
  /** Snapshot captured_at (ISO). */
  date: string;
  /** What the snapshot froze; null when the domain was not in it. */
  posture: PostureStatus | null;
}

export interface PostureStripProps {
  cells: PostureStripCell[];
  /** The live rating, drawn last as "Today". */
  current: PostureStatus;
  size?: 'md' | 'sm';
  /** Who the strip is about, for its accessible name ("Schedule"). */
  label?: string;
  /** Shown when there is no snapshot; null shows nothing. */
  emptyText?: string | null;
  className?: string;
}

const FILL: Record<PostureStatus, string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
  unassessed: 'chart-unrated-sm',
};

export function PostureStrip({
  cells,
  current,
  size = 'md',
  label,
  emptyText = 'No snapshots yet',
  className,
}: PostureStripProps) {
  const { tip, bind } = useChartTooltip();

  if (!cells.length) {
    return emptyText === null ? null : (
      <span className={cn('flex items-center text-[11px] text-ink-300', className)}>
        {emptyText}
      </span>
    );
  }

  const sm = size === 'sm';
  const cell = sm ? 'h-2 w-2 rounded-[2px]' : 'h-3.5 w-3.5 rounded-[3px]';
  const now = sm
    ? 'ml-0.5 h-2 w-3 rounded-[2px]'
    : 'ml-1 h-3.5 w-5 rounded-[3px] shadow-[0_0_0_2px_var(--paper-0),0_0_0_3px_var(--ink-300)]';
  const name = [
    ...cells.map((c) => `${formatDayMonth(c.date)} ${POSTURE_LABEL[c.posture ?? 'unassessed']}`),
    `today ${POSTURE_LABEL[current]}`,
  ].join(', ');

  return (
    <span
      role="img"
      aria-label={`${label ? `${label} rating` : 'Rating'} at each snapshot: ${name}`}
      className={cn('flex items-center gap-0.5', className)}
    >
      {cells.map((c, i) => {
        const p = c.posture ?? 'unassessed';
        return (
          <span
            key={`${c.date}-${i}`}
            className={cn('block flex-none', cell, FILL[p])}
            {...bind({ title: formatDayMonth(c.date), value: POSTURE_LABEL[p], sub: 'snapshot' })}
          />
        );
      })}
      <span
        className={cn('block flex-none', now, FILL[current])}
        {...bind({ title: 'Today', value: POSTURE_LABEL[current], sub: 'live rating' })}
      />
      <ChartTooltip tip={tip} />
    </span>
  );
}
